import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { PlayCircle } from 'lucide-react';
import { engagementsApi, runsApi, scopePoliciesApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { AuthorizationPanel } from '../components/authorization-panel';
import { ScopePolicyEditor, emptyScopePolicy } from '../components/scope-policy-editor';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import { STATUS_LABEL, STATUS_VARIANT } from './Engagements';
import type { ScopeZone } from '../lib/types';

function ScopeTab({ engagementId }: { engagementId: string }) {
  const { principal } = useAuth();
  const canWrite = principal?.role === undefined || can(principal.role, 'scopePolicy:write');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['scope-policies', engagementId], queryFn: () => scopePoliciesApi.list(engagementId) });
  const [draftZone, setDraftZone] = useState<ScopeZone>('external');
  const [draft, setDraft] = useState(emptyScopePolicy('external'));
  const [creating, setCreating] = useState(false);

  const createMutation = useMutation({
    mutationFn: () => scopePoliciesApi.create(engagementId, draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scope-policies', engagementId] });
      toast({ title: 'Scope policy saved', variant: 'success' });
      setCreating(false);
    },
    onError: (err) => toast({ title: 'Could not save scope policy', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; policy: typeof draft }) => scopePoliciesApi.update(input.id, input.policy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scope-policies', engagementId] });
      toast({ title: 'Scope policy updated', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not update scope policy', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const policies = query.data?.scopePolicies ?? [];

  return (
    <div className="space-y-6">
      {policies.map((record) => (
        <Card key={record.id}>
          <CardHeader>
            <CardTitle className="capitalize">{record.zone} scope</CardTitle>
          </CardHeader>
          <CardContent>
            <ScopePolicyEditorWithSave
              engagementId={engagementId}
              initial={record.policy}
              canWrite={canWrite}
              onSave={(policy) => updateMutation.mutate({ id: record.id, policy })}
              saving={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      ))}

      {canWrite ? (
        creating ? (
          <Card>
            <CardHeader>
              <CardTitle>New scope policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                {(['external', 'internal', 'app'] as ScopeZone[])
                  .filter((z) => !policies.some((p) => p.zone === z))
                  .map((z) => (
                    <Button
                      key={z}
                      type="button"
                      size="sm"
                      variant={draftZone === z ? 'primary' : 'outline'}
                      onClick={() => {
                        setDraftZone(z);
                        setDraft(emptyScopePolicy(z));
                      }}
                    >
                      {z}
                    </Button>
                  ))}
              </div>
              <ScopePolicyEditor value={draft} onChange={setDraft} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCreating(false)}>
                  Cancel
                </Button>
                <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
                  Save scope policy
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Button variant="outline" onClick={() => setCreating(true)}>
            Add a scope policy
          </Button>
        )
      ) : null}
    </div>
  );
}

function ScopePolicyEditorWithSave({
  initial,
  canWrite,
  onSave,
  saving,
}: {
  engagementId: string;
  initial: ReturnType<typeof emptyScopePolicy>;
  canWrite: boolean;
  onSave: (policy: ReturnType<typeof emptyScopePolicy>) => void;
  saving: boolean;
}) {
  const [policy, setPolicy] = useState(initial);
  return (
    <div className="space-y-4">
      <ScopePolicyEditor value={policy} onChange={setPolicy} />
      {canWrite ? (
        <Button size="sm" onClick={() => onSave(policy)} disabled={saving}>
          Save changes
        </Button>
      ) : null}
    </div>
  );
}

function RunsTab({ engagementId }: { engagementId: string }) {
  const { principal } = useAuth();
  const canManage = principal?.role === undefined || can(principal.role, 'run:manage');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['runs', engagementId], queryFn: () => runsApi.list(engagementId) });

  const startMutation = useMutation({
    mutationFn: () => runsApi.start(engagementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runs', engagementId] });
      toast({ title: 'Run started', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not start run', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const runs = query.data?.runs ?? [];

  return (
    <div className="space-y-4">
      {canManage ? (
        <ConfirmDialog
          trigger={
            <Button>
              <PlayCircle size={16} /> Start a run
            </Button>
          }
          title="Start a new run?"
          description="This begins non-destructive testing against everything currently in scope and authorized for this engagement. You can pause or cancel it at any time."
          confirmLabel="Start run"
          onConfirm={() => startMutation.mutateAsync()}
        />
      ) : null}

      {runs.length === 0 ? (
        <EmptyState title="No runs yet" description="Start a run once authorization is active and scope is set." />
      ) : (
        <div className="space-y-2">
          {runs.map((run) => (
            <Link key={run.id} to={`/runs/${run.id}`} className="flex items-center justify-between rounded-xl border border-border p-3 text-sm hover:bg-surface-sunken">
              <span className="text-ink-muted">{new Date(run.createdAt).toLocaleString()}</span>
              <Badge variant="brand" className="capitalize">
                {run.status}
              </Badge>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function EngagementDetail() {
  const { id } = useParams<{ id: string }>();
  const engagementId = id!;
  const query = useQuery({ queryKey: ['engagement', engagementId], queryFn: () => engagementsApi.get(engagementId) });

  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;

  const engagement = query.data!.engagement;

  return (
    <div>
      <PageHeader
        title={engagement.name}
        description="Manage authorization, scope, and runs for this engagement."
        action={
          <Badge variant={STATUS_VARIANT[engagement.status]} className="text-sm">
            {STATUS_LABEL[engagement.status]}
          </Badge>
        }
      />
      <Tabs defaultValue="authorization">
        <TabsList>
          <TabsTrigger value="authorization">Authorization</TabsTrigger>
          <TabsTrigger value="scope">Scope</TabsTrigger>
          <TabsTrigger value="runs">Runs</TabsTrigger>
        </TabsList>
        <TabsContent value="authorization">
          <AuthorizationPanel engagementId={engagementId} />
        </TabsContent>
        <TabsContent value="scope">
          <ScopeTab engagementId={engagementId} />
        </TabsContent>
        <TabsContent value="runs">
          <RunsTab engagementId={engagementId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
